// Arcade game driven by the bike: a JavaScript program run in an isolated worker (apps/web/src/games),
// which only sees the cyclist's signals and can only *request* an effort from the governor.
import type { CreationOf } from '@gruvetto/contracts';
import { defineKind, err, warn, fmtMin } from '../kind.js';

/** Reference of the games API: the same for the AI, the documentation and human authors. */
export const GAME_SDK_DOC = `The game calls Gruvetto.game({ setup, update, draw, result? }) exactly once.
Logical screen 1280 × 720 (scaled). About 60 frames per second. No access to the network, storage, DOM or external images: everything is drawn in 2D.

setup(api) → state: creates the initial state (serialisable object).
update(state, input, dt, api): advances the game by dt seconds (dt ≤ 0.1).
draw(state, g, api): draws the frame.
result(state) → { score, completed, summary }: optional, called if the player quits.

input (read-only, updated 30 times per second):
  power (W), power3s (smoothed W), cadence (rpm), hr (bpm, 0 if absent),
  ftpFrac (smoothed power / FTP: 1 = threshold, use it to adapt the difficulty so that beginners and pros play on equal terms),
  zone (1 to 7), kj (cumulative energy), wbal (anaerobic reserve 0 to 1), sprint (true on the frame a sprint starts), sprinting, sprintPeak (W),
  buttons: { left, right, fire, up, down } (true while the button is held),
  pressed: { left, right, fire, up, down } (true only on the frame of the press),
  target (W aimed at by the coupled workout, or null), targetFrac (target / FTP, or null), inTarget (true if power is within the target ±7%).
api:
  width, height (1280, 720), player { name, ftp, mass, color }, rnd() (reproducible random 0–1), time (elapsed s),
  workout (true if a workout is coupled: then follow target to reward the player),
  effort({ grade, wind, crr }): requests a resistance (gradient % from −10 to 20, wind m/s, rolling 0.002–0.015); bounded and smoothed by the engine, ignored during an ERG workout,
  toast(text, 'good' | 'bad' | 'info'), sound('coin' | 'hit' | 'boom' | 'beep' | 'win' | 'lose' | 'levelup'),
  score(n): current score displayed, end({ score, completed: true, summary: 'text' }): ends the game.
g (drawing, logical coordinates):
  g.clear(colour), g.rect(x, y, w, h, colour), g.roundRect(x, y, w, h, radius, colour), g.circle(x, y, r, colour), g.ring(x, y, r, thickness, colour),
  g.line(x1, y1, x2, y2, colour, thickness), g.poly([[x, y], …], colour), g.text(text, x, y, { size, color, align: 'left' | 'center' | 'right', weight, font }),
  g.emoji(character, x, y, size), g.bar(x, y, w, h, fraction, colour, background), g.gradient(x, y, w, h, topColour, bottomColour),
  g.alpha(a, () => …), g.save(), g.restore(), g.translate(x, y), g.rotate(angle), g.scale(s), g.ctx (raw CanvasRenderingContext2D).
CSS colours ("#ffd400", "rgba(…)").

Good practices:
- Pedalling is the main controller: speed, energy, firing power… must depend on input.ftpFrac, input.kj, input.sprint or input.wbal.
- The buttons (two controllers, left/right/fire/up/down, or the keyboard) are for choices: changing lanes, aiming, placing, triggering.
- A game lasts 5 to 30 minutes; plan a difficulty ramp and an ending (api.end). Always show a readable HUD: score, life, and the effort instruction.
- If api.workout is true, the workout imposes the power: reward inTarget rather than raw power.
- Use api.effort to make the game feel real (hill = grade 4 to 8, mud = crr 0.008, storm = wind 8), without permanent jolts.
- No infinite loops, no massive allocation on every frame: the worker is stopped if it stops responding.`;

const NETWORK = /\b(fetch|XMLHttpRequest|WebSocket|importScripts|EventSource|indexedDB|localStorage)\b/;

export const gameKind = defineKind({
  kind: 'game', label: 'Game', plural: 'Games', icon: '🕹️', verb: 'Play',
  check(c) {
    const code = c.content.code, out = [];
    if (!/Gruvetto\s*\.\s*game\s*\(/.test(code)) out.push(err('The program must call Gruvetto.game({ setup, update, draw })'));
    if (NETWORK.test(code)) out.push(warn('The program tries to access the network or storage: it will be blocked'));
    if (/\beval\s*\(|new\s+Function\s*\(/.test(code)) out.push(err('eval and new Function are forbidden'));
    if (c.content.workout && !/\btarget(Frac)?\b|inTarget/.test(code)) out.push(warn('The game claims to be workout-compatible but never reads the target (input.target)'));
    if (!c.content.mechanics.length) out.push(warn('No effort mechanic declared'));
    return out;
  },
  facts: c => [
    { label: 'Mechanics', value: c.content.mechanics.join(', ') || '—' },
    { label: 'Duration', value: c.content.duration ? fmtMin(c.content.duration) : 'open' },
    { label: 'Workout', value: c.content.workout ? 'compatible' : 'no' },
  ],
  ai: {
    summary: 'an arcade game where pedalling is the controller (tower defense, runner, rhythm, co-op, survival…)',
    effort: 'high', maxTokens: 64000,
    brief: `Write a complete, fun and finished game in modern JavaScript (no import or module), that runs with this API:
${GAME_SDK_DOC}

Fields to fill in:
- code: the complete program, self-contained, readable, commented in English (or the player's language), 150 to 900 lines.
- controls: what the buttons used are for ("fire: place a tower").
- mechanics: effort mechanics actually used among moteur (watts → speed), batterie (kJ → resource), zone (hold a zone to keep an action), explosion (sprint → action), reserve (W′bal as life), rythme (cadence on a tempo), charge (the game changes the resistance) (ids kept as is).
- workout: true if the game can dress up a workout (reads input.target / inTarget). duration: suggested duration in seconds.
Polish the rendering (background, bright colours, simple particles, HUD), the balance (difficulty relative to FTP) and the ending.`,
    example: {
      kind: 'game', name: 'Weather balloon', desc: 'Your power lifts a balloon that must slip between the clouds.', tags: ['arcade'],
      content: {
        mechanics: ['moteur', 'zone'], workout: true, duration: 600,
        controls: [{ action: 'fire', label: 'Drop ballast (small jump)' }],
        code: `// Weather balloon: altitude follows relative power; you must pass through the gaps in the clouds.
Gruvetto.game({
  setup(api) { return { y: 360, vy: 0, walls: [], next: 1.5, score: 0, lives: 3, t: 0 }; },
  update(s, input, dt, api) {
    s.t += dt;
    const want = api.workout ? (input.inTarget ? 360 : 560) : 640 - Math.min(1.6, input.ftpFrac) * 360;
    s.vy += ((want - s.y) * 2.2 - s.vy * 1.8) * dt;
    if (input.pressed.fire) s.vy -= 220;
    s.y = Math.max(40, Math.min(680, s.y + s.vy * dt));
    s.next -= dt;
    if (s.next <= 0) { s.next = Math.max(1.2, 3 - s.t / 120); s.walls.push({ x: 1340, gap: 160 + api.rnd() * 400, size: 200 }); }
    for (const w of s.walls) {
      w.x -= 260 * dt;
      if (!w.done && w.x < 300) { w.done = true; if (Math.abs(s.y - w.gap) < w.size / 2) { s.score++; api.sound('coin'); api.score(s.score); } else { s.lives--; api.sound('hit'); api.toast('Hit!', 'bad'); } }
    }
    s.walls = s.walls.filter(w => w.x > -80);
    if (s.lives <= 0) api.end({ score: s.score, completed: true, summary: s.score + ' clouds cleared' });
  },
  draw(s, g, api) {
    g.gradient(0, 0, api.width, api.height, '#5ab8ff', '#d8f1ff');
    for (const w of s.walls) { g.rect(w.x, 0, 60, w.gap - w.size / 2, '#ffffff'); g.rect(w.x, w.gap + w.size / 2, 60, 720, '#ffffff'); }
    g.emoji('🎈', 300, s.y, 64);
    g.text('Score ' + s.score + '   ' + '❤️'.repeat(s.lives), 24, 40, { size: 28, color: '#0e1116', weight: 800 });
  },
});`,
      },
    } satisfies CreationOf<'game'>,
  },
});
